import React, { useMemo, useState } from "react";

// Horizontal Gantt-like view. Each item with a `timeline` column value
// renders as a colored bar spanning its start→end range. The X axis is a
// stretchable date scale that auto-fits to the range of all items.
//
// Phase-3.5 scope: read-only positioning + click-to-open. Drag-to-reschedule
// is a follow-up.
const DAY = 86_400_000;
const MIN_BAR_WIDTH = 60;

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
// Accept Date OR ISO string — timeline column values come back from the API
// as ISO strings, and we also pass real Dates (axis ticks). Bad input renders
// as "—" instead of crashing the whole view.
function asDate(d) {
  const x = d instanceof Date ? d : new Date(d);
  return isNaN(x.getTime()) ? null : x;
}
function fmtMonth(d) {
  const x = asDate(d);
  return x ? x.toLocaleDateString(undefined, { month: "short", year: "numeric" }) : "—";
}
function fmtDay(d) {
  const x = asDate(d);
  return x ? x.toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—";
}

export default function TimelineView({ items, board, kindIndex, onCardClick, onCellCommit, readOnly }) {
  const [colId, setColId] = useState(null);
  const [drag, setDrag] = useState(null); // { itemId, mode, startX, originVal, pxPerDay }

  const timelineCols = (board?.columns || []).filter((c) => c.type === "timeline");
  const chosenCol = (colId && timelineCols.find((c) => c._id === colId)) || timelineCols[0] || null;

  // Gather items that have a populated timeline value.
  const populated = useMemo(
    () =>
      chosenCol
        ? items
            .map((it) => ({ it, val: it.column_values?.[chosenCol._id]?.value }))
            .filter((x) => x.val && x.val.start && x.val.end)
        : [],
    [items, chosenCol]
  );

  // Group items by board group for visual sections.
  const byGroup = useMemo(() => {
    const map = new Map();
    for (const { it, val } of populated) {
      const k = String(it.group_id);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push({ it, val });
    }
    return map;
  }, [populated]);

  if (!chosenCol) {
    return (
      <div className="kanban-empty-board">
        Timeline needs a <strong>timeline column</strong> (start + end dates).
        Add a column of type "timeline" to your board to get started.
      </div>
    );
  }

  if (populated.length === 0) {
    return (
      <div className="kanban-empty-board">
        No items have a value in the <strong>{chosenCol.title}</strong> column yet.
      </div>
    );
  }

  // Compute X-axis range, padded by 2 days on each side.
  const minMs = Math.min(...populated.map((x) => new Date(x.val.start).getTime()));
  const maxMs = Math.max(...populated.map((x) => new Date(x.val.end).getTime()));
  const axisStart = startOfDay(new Date(minMs - 2 * DAY));
  const axisEnd = startOfDay(new Date(maxMs + 2 * DAY));
  const totalDays = Math.max(1, Math.round((axisEnd.getTime() - axisStart.getTime()) / DAY));
  const pxPerDay = Math.max(28, Math.min(80, Math.round(900 / totalDays)));

  // Render ticks at week boundaries.
  const ticks = [];
  for (let d = new Date(axisStart); d <= axisEnd; d.setDate(d.getDate() + 7)) {
    ticks.push(new Date(d));
  }

  const widthForBar = (val) => {
    const startMs = new Date(val.start).getTime();
    const endMs = new Date(val.end).getTime();
    const span = Math.max(DAY, endMs - startMs + DAY); // inclusive end
    return Math.max(MIN_BAR_WIDTH, Math.round((span / DAY) * pxPerDay));
  };
  const leftForBar = (val) => {
    const startMs = new Date(val.start).getTime();
    return Math.round(((startMs - axisStart.getTime()) / DAY) * pxPerDay);
  };

  const totalWidth = totalDays * pxPerDay;
  const today = new Date();
  const todayLeft = Math.round(((startOfDay(today).getTime() - axisStart.getTime()) / DAY) * pxPerDay);

  return (
    <div className="timeline-view">
      <div className="cal-toolbar">
        <span className="kanban-label">Timeline column:</span>
        <select className="kanban-select" value={chosenCol._id} onChange={(e) => setColId(e.target.value)}>
          {timelineCols.map((c) => (
            <option key={c._id} value={c._id}>{c.title}</option>
          ))}
        </select>
        <span className="cal-spacer" />
        <span className="muted small">
          {fmtDay(axisStart)} – {fmtDay(axisEnd)} ({totalDays} days)
        </span>
      </div>

      <div className="timeline-scroll">
        <div className="timeline-grid" style={{ width: totalWidth }}>
          {/* Axis row */}
          <div className="timeline-axis">
            {ticks.map((d, i) => (
              <div
                key={i}
                className="timeline-tick"
                style={{ left: Math.round(((d.getTime() - axisStart.getTime()) / DAY) * pxPerDay) }}
              >
                <div className="tick-label">{fmtDay(d)}</div>
                <div className="tick-line" />
              </div>
            ))}
            {todayLeft >= 0 && todayLeft <= totalWidth && (
              <div className="timeline-today" style={{ left: todayLeft }} title="Today" />
            )}
          </div>

          {/* Group sections with bars */}
          {Array.from(byGroup.entries()).map(([gid, entries]) => {
            const g = (board.groups || []).find((x) => String(x._id) === String(gid));
            return (
              <div key={gid} className="timeline-section">
                <div className="timeline-section-head" style={{ borderLeftColor: g?.color || "#ef3a3a" }}>
                  {g?.title || "Group"}
                </div>
                {entries.map(({ it, val }) => {
                  const kd = kindIndex[it.kind];
                  // Visible bar may differ from the persisted value while a
                  // drag is in progress.
                  const liveVal =
                    drag && drag.itemId === it._id ? drag.previewVal || val : val;
                  return (
                    <div key={it._id} className="timeline-row">
                      <div
                        className="timeline-bar"
                        style={{
                          left: leftForBar(liveVal),
                          width: widthForBar(liveVal),
                          background: kd?.color || "#ef3a3a",
                          cursor: readOnly ? "pointer" : "grab",
                        }}
                        onClick={(e) => {
                          // Suppress click if a drag actually moved.
                          if (drag && drag.itemId === it._id && drag.moved) return;
                          onCardClick(it);
                        }}
                        onMouseDown={
                          readOnly
                            ? undefined
                            : (e) => startDrag(e, it, val, "move", pxPerDay, setDrag, onCellCommit, chosenCol)
                        }
                        title={`${it.title} • ${fmtDay(liveVal.start)} → ${fmtDay(liveVal.end)} (drag to reschedule)`}
                      >
                        {!readOnly && (
                          <div
                            className="timeline-handle left"
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              startDrag(e, it, val, "resize-start", pxPerDay, setDrag, onCellCommit, chosenCol);
                            }}
                          />
                        )}
                        <span className="timeline-bar-text">{it.title}</span>
                        {!readOnly && (
                          <div
                            className="timeline-handle right"
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              startDrag(e, it, val, "resize-end", pxPerDay, setDrag, onCellCommit, chosenCol);
                            }}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Drag handler. Three modes:
//   "move"          — slide both endpoints by the same delta
//   "resize-start"  — drag the left edge (changes start, keeps end)
//   "resize-end"    — drag the right edge (changes end, keeps start)
function startDrag(e, item, val, mode, pxPerDay, setDrag, onCellCommit, col) {
  e.preventDefault();
  const startX = e.clientX;
  const origStart = new Date(val.start).getTime();
  const origEnd = new Date(val.end).getTime();
  setDrag({ itemId: item._id, mode, startX, previewVal: val, moved: false });

  const onMove = (ev) => {
    const dx = ev.clientX - startX;
    const days = Math.round(dx / pxPerDay);
    let nextStart = origStart;
    let nextEnd = origEnd;
    if (mode === "move") {
      nextStart = origStart + days * DAY;
      nextEnd = origEnd + days * DAY;
    } else if (mode === "resize-start") {
      nextStart = Math.min(origStart + days * DAY, origEnd);
    } else if (mode === "resize-end") {
      nextEnd = Math.max(origEnd + days * DAY, origStart);
    }
    const previewVal = {
      start: new Date(nextStart).toISOString(),
      end: new Date(nextEnd).toISOString(),
    };
    setDrag((d) => (d ? { ...d, previewVal, moved: Math.abs(dx) > 3 } : d));
  };
  const onUp = () => {
    window.removeEventListener("mousemove", onMove);
    window.removeEventListener("mouseup", onUp);
    setDrag((d) => {
      if (!d) return null;
      if (d.moved && onCellCommit && col) {
        onCellCommit(item, col._id, d.previewVal).catch?.(() => undefined);
      }
      return null;
    });
  };
  window.addEventListener("mousemove", onMove);
  window.addEventListener("mouseup", onUp);
}
